import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, X, Barcode, RefreshCw } from "lucide-react";

/**
 * Camera-based barcode scanner using the native BarcodeDetector API
 * (supported on Chrome/Edge/Android WebView, iOS 17+).
 * Falls back to a manual keyed-entry input on unsupported browsers or if
 * camera permission is denied.
 *
 * Props:
 *   onDetected(value)   — called once with the scanned string; caller closes the modal.
 *   onClose()
 *   testId              — optional data-testid prefix
 */
export default function BarcodeScanner({ onDetected, onClose, testId = "barcode-scanner" }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const detectorRef = useRef(null);
  const rafRef = useRef(null);
  const [supported, setSupported] = useState(true);
  const [error, setError] = useState("");
  const [manual, setManual] = useState("");
  const [torchOn, setTorchOn] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function start() {
      if (typeof window === "undefined" || !("BarcodeDetector" in window)) {
        setSupported(false);
        return;
      }
      try {
        const formats = ["ean_13", "ean_8", "code_128", "code_39", "upc_a", "upc_e", "itf", "qr_code", "codabar", "data_matrix"];
        // eslint-disable-next-line no-undef
        detectorRef.current = new BarcodeDetector({ formats });
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        loop();
      } catch (e) {
        setError(e?.message || "Camera unavailable");
        setSupported(false);
      }
    }
    function loop() {
      if (cancelled) return;
      const v = videoRef.current;
      const det = detectorRef.current;
      if (v && det && v.readyState >= 2) {
        det.detect(v).then((codes) => {
          if (codes && codes[0]?.rawValue) {
            stop();
            onDetected(codes[0].rawValue);
            return;
          }
          rafRef.current = requestAnimationFrame(loop);
        }).catch(() => {
          rafRef.current = requestAnimationFrame(loop);
        });
      } else {
        rafRef.current = requestAnimationFrame(loop);
      }
    }
    function stop() {
      cancelled = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
    }
    start();
    return stop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleTorch = async () => {
    try {
      const track = streamRef.current?.getVideoTracks?.()[0];
      if (!track) return;
      const cap = track.getCapabilities?.();
      if (!cap || !cap.torch) return toast.error("Torch not supported");
      await track.applyConstraints({ advanced: [{ torch: !torchOn }] });
      setTorchOn(!torchOn);
    } catch { toast.error("Torch not supported"); }
  };

  const submitManual = () => {
    const v = manual.trim();
    if (!v) return toast.error("Enter a barcode");
    onDetected(v);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid={`${testId}-modal`}>
      <div className="hos-card w-full max-w-md p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Barcode size={18} className="text-[color:var(--gold)]"/>
            <h3 className="font-heading text-xl">Scan Pallet Barcode</h3>
          </div>
          <button onClick={onClose} className="p-1" data-testid={`${testId}-close`}><X size={16}/></button>
        </div>

        {supported ? (
          <div className="relative aspect-video bg-black rounded-md overflow-hidden">
            <video ref={videoRef} muted playsInline className="w-full h-full object-cover"/>
            <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
              <div className="w-[70%] h-24 border-2 border-[color:var(--gold)] rounded-lg shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]"/>
            </div>
            <button onClick={toggleTorch} className="absolute bottom-2 right-2 bg-black/60 text-white p-2 rounded-full">
              <RefreshCw size={14}/>
            </button>
          </div>
        ) : (
          <div className="p-4 bg-[color:var(--warning)]/10 border border-[color:var(--warning)]/40 rounded-md text-sm text-[color:var(--warning)]">
            Camera scanner not supported on this device{error ? ` (${error})` : ""}. Enter the barcode manually below.
          </div>
        )}

        <div className="mt-3">
          <label className="label-caps">Or enter manually</label>
          <div className="flex gap-2 mt-1">
            <input className="hos-input flex-1" placeholder="Scan or type barcode"
                   value={manual} onChange={(e) => setManual(e.target.value)}
                   onKeyDown={(e) => { if (e.key === "Enter") submitManual(); }}
                   autoFocus={!supported} data-testid={`${testId}-manual-input`}/>
            <button className="hos-btn-primary" onClick={submitManual} data-testid={`${testId}-manual-submit`}>Save</button>
          </div>
          <div className="text-[11px] text-[color:var(--text-secondary)] mt-2">Tip: warehouse USB scanners act as keyboards — the field above accepts them directly.</div>
        </div>
      </div>
    </div>
  );
}
